import React, { useEffect, useState } from 'react'
import {
  IconButton,
  Flex,
  Text,
  LoadingIndicator,
} from '@moonpig/launchpad-components'
import { useLocaleText } from '../ProductTile.locale'
import { IconSystemBin, IconSystemPlus } from '@moonpig/launchpad-assets'
import { styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import {
  useAddToMultiBuyList,
  useRemoveFromMultiBuyList,
} from '../../../queries/useMultiBuyList'
import { useMultiBuyContext } from '../../../contexts/multiBuy'
import {
  createGenericSelectContentEvent,
  trackGAEvent,
} from '@moonpig/web-core-analytics'
import {
  MULTI_BUY_LIST_ADD_ITEM_EVENT_NAME,
  MULTI_BUY_LIST_REMOVE_ITEM_EVENT_NAME,
  MultiBuyListAddItemEvent,
  MultiBuyListRemoveItemEvent,
} from '../../../utils/multiBuyListEvents'

type MultiBuyListButtonsProps = {
  selectedVariantSku: string
  productId: string
  multiBuyListProductCount?: number
}

const StyledContainer = styled(Flex)`
  position: absolute;
  bottom: 8px;
  right: 8px;
  z-index: 1;
  background-color: white;
  align-items: center;
  border-radius: 50px;
  gap: 8px;
  && {
    ${s({
      boxShadow: 1,
    })}
  }
`

const StyledRemoveButton = styled(IconButton)`
  && {
    ${s({
      borderRadius: 'borderRadiusCircle',
      width: 44,
      height: 44,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    })}
  }
`

const StyledAddButton = styled(IconButton)`
  && {
    ${s({
      borderRadius: 'borderRadiusCircle',
      width: 44,
      height: 44,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    })}
  }
`

const ShoppingListLoadingIcon = () => (
  <LoadingIndicator size={8} color="colorInteractionLoading" />
)
const DeleteLoadingIcon = () => (
  <LoadingIndicator
    label={'Removing'}
    size={8}
    color="colorInteractionLoading"
  />
)

export const MultiBuyListButtons = ({
  selectedVariantSku,
  multiBuyListProductCount = 0,
  productId,
}: MultiBuyListButtonsProps) => {
  const localiseText = useLocaleText()
  const [, { setError }] = useMultiBuyContext()

  const [shoppingListQuantity, setShoppingListQuantity] = useState(
    () => multiBuyListProductCount,
  )
  const { addToMultiBuyList, loading: isAddingToMultiBuyList } =
    useAddToMultiBuyList()
  const { removeFromMultiBuyList, loading: isRemovingFromMultiBuyList } =
    useRemoveFromMultiBuyList()

  useEffect(() => {
    setShoppingListQuantity(multiBuyListProductCount)
  }, [multiBuyListProductCount])

  useEffect(() => {
    const handleRemoveItemEvent = (event: Event) => {
      const { detail } = event as CustomEvent<MultiBuyListRemoveItemEvent>

      if (detail?.productId.toLowerCase() === productId.toLowerCase()) {
        setShoppingListQuantity(prev => Math.max(0, prev - 1))
      }
    }

    window.addEventListener(
      MULTI_BUY_LIST_REMOVE_ITEM_EVENT_NAME,
      handleRemoveItemEvent,
    )

    return () => {
      window.removeEventListener(
        MULTI_BUY_LIST_REMOVE_ITEM_EVENT_NAME,
        handleRemoveItemEvent,
      )
    }
  }, [productId])

  useEffect(() => {
    const handleAddItemEvent = (event: Event) => {
      const { detail } = event as CustomEvent<MultiBuyListAddItemEvent>

      if (detail?.productId.toLowerCase() === productId.toLowerCase()) {
        setShoppingListQuantity(prev => Math.max(0, prev + 1))
      }
    }

    window.addEventListener(
      MULTI_BUY_LIST_ADD_ITEM_EVENT_NAME,
      handleAddItemEvent,
    )

    return () => {
      window.removeEventListener(
        MULTI_BUY_LIST_ADD_ITEM_EVENT_NAME,
        handleAddItemEvent,
      )
    }
  }, [productId])

  const isMutationPending = isAddingToMultiBuyList || isRemovingFromMultiBuyList

  const addToShoppingList = async (eventLabel: string) => {
    setError({ error: false, errorMessage: undefined })

    const response = await addToMultiBuyList(selectedVariantSku)

    if (response.error) {
      setError({ error: true, errorMessage: response.error.errorMessage })
    }

    if (!response.error) {
      if (response.productMultiBuyListCount !== undefined) {
        setShoppingListQuantity(response.productMultiBuyListCount)
      }

      trackGAEvent(
        createGenericSelectContentEvent({
          eventCategory: 'multibuy list',
          eventAction: 'added',
          eventLabel,
        }),
      )
    }
  }

  const handleAddToShoppingList = async () => {
    await addToShoppingList?.('card product tile cta')
  }

  const removeFromShoppingList = async (eventLabel: string) => {
    setError({ error: false, errorMessage: undefined })
    const response = await removeFromMultiBuyList({ sku: selectedVariantSku })

    if (response.error) {
      setError({ error: true, errorMessage: response.error.errorMessage })
    }

    if (!response.error) {
      if (response.productMultiBuyListCount !== undefined) {
        setShoppingListQuantity(response.productMultiBuyListCount)
      }

      trackGAEvent(
        createGenericSelectContentEvent({
          eventCategory: 'multibuy list',
          eventAction: 'removed',
          eventLabel,
        }),
      )
    }
  }

  const handleRemoveFromShoppingList = async () => {
    await removeFromShoppingList?.('card product tile cta')
  }

  return (
    <StyledContainer>
      {shoppingListQuantity > 0 && (
        <StyledRemoveButton
          icon={isRemovingFromMultiBuyList ? DeleteLoadingIcon : IconSystemBin}
          label={localiseText('card_product_tile.remove_from_shopping_list')}
          disabled={isMutationPending}
          onClick={handleRemoveFromShoppingList}
        />
      )}
      {shoppingListQuantity > 0 && (
        <Text typography="typeBodyLabel">{shoppingListQuantity}</Text>
      )}
      {
        <StyledAddButton
          label={localiseText('card_product_tile.add_to_shopping_list')}
          icon={
            isAddingToMultiBuyList ? ShoppingListLoadingIcon : IconSystemPlus
          }
          disabled={isMutationPending}
          onClick={handleAddToShoppingList}
        />
      }
    </StyledContainer>
  )
}
